import { useEffect, useRef } from 'react';
import { useScrollLock } from '../../_Hooks/useScrollLock';
import { confirm } from '../Confirm/AppConfirmHost';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';

const BLOCK = 'LStudio__app_modal';

/*
    🔥 MODAL GENÉRICO

    Distinto del Confirm: eso pregunta sí o no. Esto muestra CONTENIDO
    —un formulario, un detalle, una galería— y el usuario interactúa
    adentro.

    ⚠️ LAS CUATRO COSAS QUE UN MODAL DEBE HACER Y CASI NINGUNO HACE

    1. ATRAPAR EL FOCO. Con Tab no se puede salir del modal hacia la
       página de atrás. Si se pudiera, un usuario de teclado quedaría
       escribiendo en campos que no ve.

    2. DEVOLVER EL FOCO AL CERRAR. Vuelve al elemento que lo abrió.
       Sin esto el foco se va al <body> y hay que volver a tabular
       desde el principio.

    3. BLOQUEAR EL SCROLL DEL FONDO, compensando el ancho de la barra
       para que la página no salte al abrir.

    4. NO CERRARSE ACCIDENTALMENTE. En NOTEBOOK el backdrop y Escape
       no descartan un modal: el cierre se hace con controles explícitos.
       Si hay cambios sin guardar, la X igualmente pide confirmación.
*/

const FOCUSABLE = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled])',
    'select:not([disabled])', 'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

export const AppModal = ({
    isOpen,
    onClose,
    title,
    description,
    children,
    footer = null,
    size = 'md',                    // sm | md | lg | xl | full
    position = 'center',            // center | top | drawer-right | drawer-left
    isDismissible = true,           // permite cierres explicitos (X / acciones del modal)
    closeOnBackdrop = false,         // NOTEBOOK: nunca cerrar por clic fuera salvo override explicito
    closeOnEscape = false,           // NOTEBOOK: Escape no cierra modales por defecto
    isDirty = false,                // hay cambios sin guardar
    dirtyMessage = 'Si salís ahora se pierde lo que cargaste. ¿Querés descartarlo?',
    hasCloseButton = true,
    className = '',
}) => {

    const REF__useRefDialog = useRef(null);
    const REF__useRefOpener = useRef(null);

    // 🔥 Guarda quién abrió el modal para devolverle el foco al cerrar
    useEffect(() => {

        if (!isOpen) return;

        REF__useRefOpener.current = document.activeElement;

        requestAnimationFrame(() => {
            const FIRST = REF__useRefDialog.current?.querySelector(FOCUSABLE);
            (FIRST ?? REF__useRefDialog.current)?.focus();
        });

        return () => REF__useRefOpener.current?.focus?.();

    }, [isOpen]);

    /*
        🔥 Bloquea el scroll compensando el ancho de la barra.
        Sin la compensación, la página salta unos píxeles al abrir.
    */
    /*
        🔥 Bloqueo del scroll de la página mientras el modal está abierto.

        ⚠️ Lo hace el hook compartido. Antes este componente guardaba y
           restauraba el overflow por su cuenta, igual que otros cuatro:
           con dos abiertos a la vez, el primero en cerrarse devolvía el
           scroll aunque el otro siguiera abierto.

        El hook también compensa el ancho de la barra —para que la
        página no salte al abrir— y frena el scroll en Safari de iPhone,
        donde overflow: hidden no alcanza.
    */
    useScrollLock(isOpen);

    const onTryClose = async () => {

        if (!isDismissible) return;

        /*
            ⚠️ Antes usaba window.confirm, el cartel gris del navegador.
            Ahora usa el confirm del template, para que se vea igual que
            el resto de la app y respete el nivel de estilo elegido.
        */
        if (isDirty) {

            const IS_CONFIRMED = await confirm.warning('Cambios sin guardar', dirtyMessage, {
                confirmText: 'Descartar',
                cancelText: 'Seguir editando',
            });

            if (!IS_CONFIRMED) return;
        }

        onClose?.();
    };

    // 🔥 Escape y trampa de foco
    useEffect(() => {

        if (!isOpen) return;

        const onKeyDown = (event) => {

            if (event.key === 'Escape' && closeOnEscape) {
                event.preventDefault();
                return onTryClose();
            }

            if (event.key !== 'Tab') return;

            const ITEMS = REF__useRefDialog.current?.querySelectorAll(FOCUSABLE);

            if (!ITEMS?.length) return;

            const FIRST = ITEMS[0];
            const LAST = ITEMS[ITEMS.length - 1];

            // Al llegar al último con Tab, vuelve al primero (y al revés)
            if (!event.shiftKey && document.activeElement === LAST) {
                event.preventDefault();
                FIRST.focus();
            }

            if (event.shiftKey && document.activeElement === FIRST) {
                event.preventDefault();
                LAST.focus();
            }
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);

    }, [isOpen, isDirty, isDismissible, closeOnEscape]);   // eslint-disable-line react-hooks/exhaustive-deps

    if (!isOpen) return null;

    return createPortal(

        <div className={`${BLOCK}__backdrop`} data-position={position}
            onMouseDown={closeOnBackdrop ? onTryClose : undefined}>

            <div ref={REF__useRefDialog} tabIndex={-1}
                className={`${BLOCK} ${className}`}
                data-size={size} data-position={position}
                role="dialog" aria-modal="true"
                aria-label={typeof title === 'string' ? title : undefined}
                onMouseDown={(event) => event.stopPropagation()} >

                {
                    (title || hasCloseButton) && (
                        <header className={`${BLOCK}__head`}>

                            <div className={`${BLOCK}__head_text`}>
                                {title && <h2 className={`${BLOCK}__title`}>{title}</h2>}
                                {description && <p className={`${BLOCK}__description`}>{description}</p>}
                            </div>

                            {
                                hasCloseButton && (
                                    <button type="button" className={`${BLOCK}__close`}
                                        aria-label="Cerrar" onClick={onTryClose} >
                                        <FaTimes />
                                    </button>
                                )
                            }

                        </header>
                    )
                }

                <div className={`${BLOCK}__body`}>{children}</div>

                {footer && <footer className={`${BLOCK}__footer`}>{footer}</footer>}

            </div>

        </div>,

        document.body,
    );
};
